import { cn } from '@invent/wl-ui-kit-next';
import { Button } from '@invent/wl-ui-kit-next/ui';
import type * as React from 'react';
import { Link, Navigate } from 'react-router-dom';

import { HOME_ROUTE, REGISTRATION_ROUTE, SIGN_IN_REQUEST_ROUTE } from '../constants';
import { useAuth } from '../auth-context';
import { beginSignIn } from '../idp';
import { AuthShell } from './auth-shell';
import { AuthSplash } from './auth-splash';
import { IdpUnreachable, useIdpDeparture } from './idp-departure';

/**
 * `/login` — the front door's sign-in: one button to Invent's IdP, and the two ways for somebody
 * the IdP cannot sign in yet — an invitation asked for, and registration.
 *
 * **A page, not a redirect** (the IdP sign-in design, decision 4). A new advisor needs the
 * registration link and a migrated advisor the request link before any IdP page, and a page that
 * left for the IdP on load would show neither. Rejected: sending the browser to the IdP at once.
 *
 * The press leaves for the IdP (`beginSignIn`), which sends the browser back to `/auth/callback`;
 * that page opens the session and lands where the guard bounced the visitor from. A press that
 * cannot leave — the IdP's discovery document unread — is said here.
 */
export function LoginPage() {
  const { status, error } = useAuth();
  const { leaving, unreachable, leave } = useIdpDeparture();

  if (status === 'pending') {
    return <AuthSplash />;
  }

  if (status === 'authenticated') {
    return <Navigate to={HOME_ROUTE} replace />;
  }

  return (
    <AuthShell title="Sign in">
      <h1 className="text-2xl text-[var(--color-royal-blue)]">Sign in</h1>
      <p className="mt-2 mb-6 text-sm text-muted-foreground">
        Complete and update your ranking survey.
      </p>

      <div className="grid gap-5">
        {error && (
          // The "session expired" message left behind when a session was dropped.
          <p role="alert" className="text-sm font-medium text-destructive">
            {error.message}
          </p>
        )}

        {unreachable && <IdpUnreachable />}

        <Button
          type="button"
          disabled={leaving}
          className="w-full"
          onClick={() => void leave(() => beginSignIn())}
        >
          {leaving ? 'Opening sign-in…' : 'Sign in'}
        </Button>
      </div>

      {/* For an account that signs in nowhere yet — a migrated advisor's, above all. */}
      <p className="mt-6 text-center text-sm text-muted-foreground">
        No invitation yet?{' '}
        <Link to={SIGN_IN_REQUEST_ROUTE} className="hover:text-[var(--color-royal-blue)]">
          Ask for one
        </Link>
      </p>

      {/* The way in for somebody with no account at all, which makes this screen the door
          rather than only the sign-in. Registration asks for the number first, so an account is
          what a run ends with, not what one starts from. */}
      <div className="mt-6 border-t border-border pt-6 text-center text-sm">
        <p className="text-muted-foreground">New to the Veritant portal?</p>
        <Button asChild variant="outline" className="mt-2 w-full">
          <Link to={REGISTRATION_ROUTE}>Create an account</Link>
        </Button>
      </div>
    </AuthShell>
  );
}

/** A word the page that sent the visitor to `/sign-in/request` left in the route's state, said
 *  above what the page offers. */
export function RouteNote({ children }: { children: React.ReactNode }) {
  return (
    <p
      role="status"
      className={cn(
        'mb-6 rounded-md border border-border px-3 py-2 text-sm',
        'bg-[var(--color-slight-blue)]/40 text-[var(--color-royal-blue)]'
      )}
    >
      {children}
    </p>
  );
}
